// src/utils/globalActivityLogger.js

import { collection, doc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Comprehensive Global Activity Logger
 * Tracks ALL user activities across the entire application
 */

// Global Activity Types
export const GLOBAL_ACTIVITY_TYPES = {
  // Authentication
  USER_LOGIN: 'kullanici_giris',
  USER_LOGOUT: 'kullanici_cikis',
  USER_REGISTER: 'kullanici_kayit',
  PASSWORD_CHANGE: 'sifre_degisim',
  
  // Orders
  ORDER_CREATED: 'siparis_olusturuldu',
  ORDER_UPDATED: 'siparis_guncellendi',
  ORDER_DELETED: 'siparis_silindi',
  ORDER_VIEWED: 'siparis_goruntulendi',
  ORDER_EXPORTED: 'siparis_disaaktar',
  ORDER_STATUS_CHANGED: 'siparis_durum_degisti',
  ORDER_PAYMENT_RECEIVED: 'siparis_odeme_alindi',
  ORDER_SHIPMENT_CREATED: 'siparis_sevkiyat_olusturuldu',
  
  // Customers
  CUSTOMER_CREATED: 'musteri_olusturuldu',
  CUSTOMER_UPDATED: 'musteri_guncellendi',
  CUSTOMER_DELETED: 'musteri_silindi',
  CUSTOMER_VIEWED: 'musteri_goruntulendi',
  CUSTOMER_EXPORTED: 'musteri_disaaktar',
  
  // Products/Stock
  PRODUCT_CREATED: 'urun_olusturuldu',
  PRODUCT_UPDATED: 'urun_guncellendi',
  PRODUCT_DELETED: 'urun_silindi',
  PRODUCT_VIEWED: 'urun_goruntulendi',
  STOCK_ENTRY: 'stok_giris',
  STOCK_EXIT: 'stok_cikis',
  STOCK_ADJUSTMENT: 'stok_duzeltme',
  
  // Invoices
  INVOICE_CREATED: 'fatura_olusturuldu',
  INVOICE_UPDATED: 'fatura_guncellendi',
  INVOICE_DELETED: 'fatura_silindi',
  INVOICE_VIEWED: 'fatura_goruntulendi',
  INVOICE_EXPORTED: 'fatura_disaaktar',
  
  // Purchase Invoices
  PURCHASE_INVOICE_CREATED: 'alis_faturasi_olusturuldu',
  PURCHASE_INVOICE_UPDATED: 'alis_faturasi_guncellendi',
  PURCHASE_INVOICE_DELETED: 'alis_faturasi_silindi',
  PURCHASE_INVOICE_VIEWED: 'alis_faturasi_goruntulendi',
  
  // Suppliers
  SUPPLIER_CREATED: 'tedarikci_olusturuldu',
  SUPPLIER_UPDATED: 'tedarikci_guncellendi',
  SUPPLIER_DELETED: 'tedarikci_silindi',
  SUPPLIER_VIEWED: 'tedarikci_goruntulendi',
  
  // Reports
  REPORT_GENERATED: 'rapor_olusturuldu',
  REPORT_EXPORTED: 'rapor_disaaktar',
  REPORT_VIEWED: 'rapor_goruntulendi',
  
  // Cash & Bank
  TRANSACTION_CREATED: 'islem_olusturuldu',
  TRANSACTION_UPDATED: 'islem_guncellendi',
  TRANSACTION_DELETED: 'islem_silindi',
  
  // Settings
  SETTINGS_VIEWED: 'ayarlar_goruntulendi',
  SETTINGS_UPDATED: 'ayarlar_guncellendi',
  COMPANY_INFO_UPDATED: 'sirket_bilgi_guncellendi',
  
  // Dashboard
  DASHBOARD_VIEWED: 'dashboard_goruntulendi',
  
  // Todo List
  TODO_CREATED: 'gorev_olusturuldu',
  TODO_UPDATED: 'gorev_guncellendi',
  TODO_DELETED: 'gorev_silindi',
  TODO_COMPLETED: 'gorev_tamamlandi',
  
  // System
  PAGE_VISITED: 'sayfa_ziyaret',
  SEARCH_PERFORMED: 'arama_yapildi',
  FILTER_APPLIED: 'filtre_uygulandi',
  EXPORT_PERFORMED: 'disa_aktar',
  IMPORT_PERFORMED: 'ice_aktar',
  
  // Subscription
  SUBSCRIPTION_UPGRADED: 'abonelik_yukseltme',
  SUBSCRIPTION_DOWNGRADED: 'abonelik_dusurmey',
  PAYMENT_MADE: 'odeme_yapildi',
  
  // Errors & Security
  ERROR_OCCURRED: 'hata_olustu',
  UNAUTHORIZED_ACCESS: 'yetkisiz_erisim',
  FAILED_LOGIN: 'basarisiz_giris'
};

// Activity Messages in Turkish
export const GLOBAL_ACTIVITY_MESSAGES = {
  [GLOBAL_ACTIVITY_TYPES.USER_LOGIN]: 'Kullanıcı giriş yaptı',
  [GLOBAL_ACTIVITY_TYPES.USER_LOGOUT]: 'Kullanıcı çıkış yaptı',
  [GLOBAL_ACTIVITY_TYPES.USER_REGISTER]: 'Yeni kullanıcı kaydı',
  [GLOBAL_ACTIVITY_TYPES.PASSWORD_CHANGE]: 'Şifre değiştirildi',
  
  [GLOBAL_ACTIVITY_TYPES.ORDER_CREATED]: 'Sipariş oluşturuldu',
  [GLOBAL_ACTIVITY_TYPES.ORDER_UPDATED]: 'Sipariş güncellendi',
  [GLOBAL_ACTIVITY_TYPES.ORDER_DELETED]: 'Sipariş silindi',
  [GLOBAL_ACTIVITY_TYPES.ORDER_VIEWED]: 'Sipariş görüntülendi',
  [GLOBAL_ACTIVITY_TYPES.ORDER_EXPORTED]: 'Sipariş dışa aktarıldı',
  
  [GLOBAL_ACTIVITY_TYPES.CUSTOMER_CREATED]: 'Müşteri oluşturuldu',
  [GLOBAL_ACTIVITY_TYPES.CUSTOMER_UPDATED]: 'Müşteri güncellendi',
  [GLOBAL_ACTIVITY_TYPES.CUSTOMER_DELETED]: 'Müşteri silindi',
  [GLOBAL_ACTIVITY_TYPES.CUSTOMER_VIEWED]: 'Müşteri görüntülendi',
  [GLOBAL_ACTIVITY_TYPES.CUSTOMER_EXPORTED]: 'Müşteri listesi dışa aktarıldı',
  
  [GLOBAL_ACTIVITY_TYPES.PRODUCT_CREATED]: 'Ürün oluşturuldu',
  [GLOBAL_ACTIVITY_TYPES.PRODUCT_UPDATED]: 'Ürün güncellendi',
  [GLOBAL_ACTIVITY_TYPES.PRODUCT_DELETED]: 'Ürün silindi',
  [GLOBAL_ACTIVITY_TYPES.PRODUCT_VIEWED]: 'Ürün görüntülendi',
  [GLOBAL_ACTIVITY_TYPES.STOCK_ENTRY]: 'Stok girişi yapıldı',
  [GLOBAL_ACTIVITY_TYPES.STOCK_EXIT]: 'Stok çıkışı yapıldı',
  
  [GLOBAL_ACTIVITY_TYPES.INVOICE_CREATED]: 'Fatura oluşturuldu',
  [GLOBAL_ACTIVITY_TYPES.INVOICE_UPDATED]: 'Fatura güncellendi',
  [GLOBAL_ACTIVITY_TYPES.INVOICE_DELETED]: 'Fatura silindi',
  [GLOBAL_ACTIVITY_TYPES.INVOICE_VIEWED]: 'Fatura görüntülendi',
  
  [GLOBAL_ACTIVITY_TYPES.PURCHASE_INVOICE_CREATED]: 'Alış faturası oluşturuldu',
  [GLOBAL_ACTIVITY_TYPES.PURCHASE_INVOICE_UPDATED]: 'Alış faturası güncellendi',
  [GLOBAL_ACTIVITY_TYPES.PURCHASE_INVOICE_DELETED]: 'Alış faturası silindi',
  
  [GLOBAL_ACTIVITY_TYPES.SUPPLIER_CREATED]: 'Tedarikçi oluşturuldu',
  [GLOBAL_ACTIVITY_TYPES.SUPPLIER_UPDATED]: 'Tedarikçi güncellendi',
  [GLOBAL_ACTIVITY_TYPES.SUPPLIER_DELETED]: 'Tedarikçi silindi',
  
  [GLOBAL_ACTIVITY_TYPES.REPORT_GENERATED]: 'Rapor oluşturuldu',
  [GLOBAL_ACTIVITY_TYPES.REPORT_EXPORTED]: 'Rapor dışa aktarıldı',
  [GLOBAL_ACTIVITY_TYPES.REPORT_VIEWED]: 'Rapor görüntülendi',
  
  [GLOBAL_ACTIVITY_TYPES.TRANSACTION_CREATED]: 'Mali işlem oluşturuldu',
  [GLOBAL_ACTIVITY_TYPES.TRANSACTION_UPDATED]: 'Mali işlem güncellendi',
  [GLOBAL_ACTIVITY_TYPES.TRANSACTION_DELETED]: 'Mali işlem silindi',
  
  [GLOBAL_ACTIVITY_TYPES.SETTINGS_VIEWED]: 'Ayarlar görüntülendi',
  [GLOBAL_ACTIVITY_TYPES.SETTINGS_UPDATED]: 'Ayarlar güncellendi',
  [GLOBAL_ACTIVITY_TYPES.COMPANY_INFO_UPDATED]: 'Şirket bilgileri güncellendi',
  
  [GLOBAL_ACTIVITY_TYPES.DASHBOARD_VIEWED]: 'Dashboard görüntülendi',
  
  [GLOBAL_ACTIVITY_TYPES.TODO_CREATED]: 'Görev oluşturuldu',
  [GLOBAL_ACTIVITY_TYPES.TODO_UPDATED]: 'Görev güncellendi',
  [GLOBAL_ACTIVITY_TYPES.TODO_DELETED]: 'Görev silindi',
  [GLOBAL_ACTIVITY_TYPES.TODO_COMPLETED]: 'Görev tamamlandı',
  
  [GLOBAL_ACTIVITY_TYPES.PAGE_VISITED]: 'Sayfa ziyaret edildi',
  [GLOBAL_ACTIVITY_TYPES.SEARCH_PERFORMED]: 'Arama yapıldı',
  [GLOBAL_ACTIVITY_TYPES.FILTER_APPLIED]: 'Filtre uygulandı',
  [GLOBAL_ACTIVITY_TYPES.EXPORT_PERFORMED]: 'Dışa aktarım yapıldı',
  
  [GLOBAL_ACTIVITY_TYPES.ERROR_OCCURRED]: 'Hata oluştu',
  [GLOBAL_ACTIVITY_TYPES.UNAUTHORIZED_ACCESS]: 'Yetkisiz erişim denemesi',
  [GLOBAL_ACTIVITY_TYPES.FAILED_LOGIN]: 'Başarısız giriş denemesi'
};

/**
 * Get user's IP address
 */
const getUserIP = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    return 'Bilinmiyor';
  }
};

/**
 * Get browser and device information
 */
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  const language = navigator.language;
  
  return {
    userAgent: userAgent.substring(0, 200), // Limit length
    platform,
    language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  };
};

/**
 * Log global activity to Firestore
 * @param {object} params - Activity parameters
 * @param {string} params.companyId - Company ID
 * @param {object} params.user - Current user object
 * @param {string} params.activityType - Type of activity (from GLOBAL_ACTIVITY_TYPES)
 * @param {string} params.entityType - Type of entity (order, customer, product, etc.)
 * @param {string} params.entityId - ID of the entity (optional)
 * @param {string} params.entityName - Name of the entity (optional)
 * @param {object} params.details - Additional details about the activity
 * @param {object} params.batch - Optional Firestore batch to use
 * @returns {Promise} - Promise that resolves when activity is logged
 */
export const logGlobalActivity = async ({
  companyId,
  user,
  activityType,
  entityType = 'system',
  entityId = null,
  entityName = null,
  details = {},
  batch = null
}) => {
  if (!companyId || !user || !activityType) {
    console.error('Missing required parameters for global activity logging');
    return;
  }

  try {
    const shouldCommitBatch = !batch;
    const activityBatch = batch || writeBatch(db);
    
    // Get additional context information
    const deviceInfo = getDeviceInfo();
    const ipAddress = details.ipAddress || await getUserIP();
    
    // Create comprehensive activity log entry
    const activityData = {
      // User Information
      userId: user.uid,
      userEmail: user.email,
      userName: user.email?.split('@')[0] || 'Bilinmeyen Kullanıcı',
      userRole: details.userRole || user.role || 'kullanıcı',
      
      // Activity Information
      activityType,
      activityMessage: GLOBAL_ACTIVITY_MESSAGES[activityType] || 'Bilinmeyen işlem',
      entityType,
      entityId,
      entityName,
      
      // Timestamp Information
      timestamp: serverTimestamp(),
      formattedDate: new Date().toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      
      // Session Information
      sessionId: details.sessionId || `session_${Date.now()}_${user.uid.substring(0, 6)}`,
      
      // Device & Network Information
      ipAddress,
      deviceInfo,
      
      // Activity Details
      details: {
        ...details,
        url: window.location.href,
        referrer: document.referrer || 'Direct',
        duration: details.duration || null, // For page visits
        success: details.success !== undefined ? details.success : true
      },
      
      // Metadata
      actionSource: 'web_app',
      version: '1.0',
      companyId
    };

    // Log to company-wide activity collection
    const companyActivityRef = doc(collection(db, 'companies', companyId, 'globalActivities'));
    activityBatch.set(companyActivityRef, activityData);
    
    // Also log to user-specific activity collection for easy user reports
    const userActivityRef = doc(collection(db, 'companies', companyId, 'userActivities', user.uid, 'activities'));
    activityBatch.set(userActivityRef, activityData);
    
    if (shouldCommitBatch) {
      await activityBatch.commit();
    }

    console.log(`Global activity logged: ${activityType} by ${user.email}`);
  } catch (error) {
    console.error('Error logging global activity:', error);
  }
};

/**
 * Helper functions for specific activities
 */

// Page visit tracking
export const logPageVisit = async (companyId, user, pageName, pageUrl) => {
  await logGlobalActivity({
    companyId,
    user,
    activityType: GLOBAL_ACTIVITY_TYPES.PAGE_VISITED,
    entityType: 'page',
    entityName: pageName,
    details: {
      pageUrl,
      userRole: user.role || 'kullanıcı'
    }
  });
};

// Authentication activities
export const logUserLogin = async (companyId, user) => {
  await logGlobalActivity({
    companyId,
    user,
    activityType: GLOBAL_ACTIVITY_TYPES.USER_LOGIN,
    entityType: 'authentication',
    details: {
      loginMethod: 'email',
      userRole: user.role || 'kullanıcı'
    }
  });
};

export const logUserLogout = async (companyId, user, sessionDuration) => {
  await logGlobalActivity({
    companyId,
    user,
    activityType: GLOBAL_ACTIVITY_TYPES.USER_LOGOUT,
    entityType: 'authentication',
    details: {
      sessionDuration,
      userRole: user.role || 'kullanıcı'
    }
  });
};

// Search and filter activities
export const logSearchPerformed = async (companyId, user, searchTerm, searchContext, resultsCount) => {
  await logGlobalActivity({
    companyId,
    user,
    activityType: GLOBAL_ACTIVITY_TYPES.SEARCH_PERFORMED,
    entityType: 'search',
    details: {
      searchTerm,
      searchContext,
      resultsCount,
      userRole: user.role || 'kullanıcı'
    }
  });
};

export const logFilterApplied = async (companyId, user, filterType, filterValue, context) => {
  await logGlobalActivity({
    companyId,
    user,
    activityType: GLOBAL_ACTIVITY_TYPES.FILTER_APPLIED,
    entityType: 'filter',
    details: {
      filterType,
      filterValue,
      context,
      userRole: user.role || 'kullanıcı'
    }
  });
};

// Export activities
export const logExportPerformed = async (companyId, user, exportType, entityType, recordCount, format) => {
  await logGlobalActivity({
    companyId,
    user,
    activityType: GLOBAL_ACTIVITY_TYPES.EXPORT_PERFORMED,
    entityType,
    details: {
      exportType,
      recordCount,
      format,
      userRole: user.role || 'kullanıcı'
    }
  });
};

// Error tracking
export const logError = async (companyId, user, errorType, errorMessage, errorContext) => {
  await logGlobalActivity({
    companyId,
    user,
    activityType: GLOBAL_ACTIVITY_TYPES.ERROR_OCCURRED,
    entityType: 'error',
    details: {
      errorType,
      errorMessage: errorMessage?.substring(0, 500), // Limit error message length
      errorContext,
      success: false,
      userRole: user.role || 'kullanıcı'
    }
  });
};

const globalActivityLogger = {
  logGlobalActivity,
  logPageVisit,
  logUserLogin,
  logUserLogout,
  logSearchPerformed,
  logFilterApplied,
  logExportPerformed,
  logError,
  GLOBAL_ACTIVITY_TYPES,
  GLOBAL_ACTIVITY_MESSAGES
};

export default globalActivityLogger;